import React, { useState, useEffect } from 'react';
import { Link2, Sparkles, Trash2, Calendar, Tag, ExternalLink, Loader2, Brain, Copy, Check, RefreshCw, Edit3, Save, X, MessageSquare, Search, Filter, Download, ChevronDown, ChevronUp, Clock, Database, Globe, Server, Activity, CheckCircle, XCircle, AlertCircle, ArrowUp, ArrowDown, TrendingUp, BarChart3, Target } from 'lucide-react';
import { API_CONFIG } from '../config/api';

interface ResearchLink {
  id: string;
  url: string;
  title: string;
  description?: string;
  faviconUrl?: string;
  ogImage?: string;
  siteName?: string;
  aiExpansion?: string;
  tags: string[];
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  utmData?: Record<string, string>;
  createdAt: string;
  processedAt?: string;
}

interface Note {
  id: string;
  linkId: string;
  content: string;
  createdAt: string;
  updatedAt?: string;
}

interface Operation {
  id: string;
  type: 'fetch' | 'create' | 'update' | 'delete' | 'ai-process' | 'poll';
  method?: string;
  endpoint: string;
  status: 'pending' | 'success' | 'error';
  timestamp: string;
  duration?: number;
  details?: string;
  error?: string;
}

export default function ResearchLinks() {
  const [links, setLinks] = useState<ResearchLink[]>([]);
  const [filteredLinks, setFilteredLinks] = useState<ResearchLink[]>([]);
  const [newUrl, setNewUrl] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedLink, setSelectedLink] = useState<ResearchLink | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [reprocessingIds, setReprocessingIds] = useState<Set<string>>(new Set());
  const [customPrompt, setCustomPrompt] = useState('');
  const [showCustomPrompt, setShowCustomPrompt] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, Note[]>>({});
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'title'>('newest');
  const [selectedLinks, setSelectedLinks] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [operations, setOperations] = useState<Operation[]>([]);
  const [showOperations, setShowOperations] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [buttonClickTime, setButtonClickTime] = useState<string | null>(null);

  useEffect(() => {
    fetchLinks();
  }, []);

  useEffect(() => {
    filterAndSortLinks();
  }, [links, searchQuery, selectedTags, sortBy]);

  const logOperation = (op: Omit<Operation, 'id' | 'timestamp'>) => {
    const newOp: Operation = {
      ...op,
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString()
    };
    setOperations(prev => [newOp, ...prev].slice(0, 50)); // Keep last 50 operations
    return newOp.id;
  };

  const updateOperation = (id: string, updates: Partial<Operation>) => {
    setOperations(prev => prev.map(op => 
      op.id === id 
        ? { ...op, ...updates, duration: Date.now() - new Date(op.timestamp).getTime() }
        : op
    ));
  };

  const fetchLinks = async () => {
    setIsLoading(true);
    setError(null);
    
    const opId = logOperation({
      type: 'fetch',
      method: 'GET',
      endpoint: '/research-links',
      status: 'pending',
      details: 'Fetching all research links...'
    });
    
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/research-links`);
      if (response.ok) {
        const data = await response.json();
        setLinks(data);
        updateOperation(opId, {
          status: 'success',
          details: `Fetched ${data.length} links successfully`
        });
      } else {
        throw new Error('Failed to fetch links');
      }
    } catch (error) {
      console.error('Error fetching links:', error);
      setError('Failed to load research links. Please try again.');
      updateOperation(opId, {
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortLinks = () => {
    let filtered = [...links];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(link => 
        link.title?.toLowerCase().includes(query) ||
        link.description?.toLowerCase().includes(query) ||
        link.url.toLowerCase().includes(query) ||
        link.aiExpansion?.toLowerCase().includes(query)
      );
    }
    
    // Tag filter
    if (selectedTags.size > 0) {
      filtered = filtered.filter(link => 
        link.tags.some(tag => selectedTags.has(tag))
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        default:
          return 0;
      }
    });
    
    setFilteredLinks(filtered);
  };

  const getAllTags = () => {
    const tagSet = new Set<string>();
    links.forEach(link => {
      link.tags.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  };

  const toggleTag = (tag: string) => {
    const newTags = new Set(selectedTags);
    if (newTags.has(tag)) {
      newTags.delete(tag);
    } else {
      newTags.add(tag);
    }
    setSelectedTags(newTags);
  };

  const exportData = () => {
    const exportObj = {
      links: links.map(link => ({
        ...link,
        notes: notes[link.id] || []
      })),
      exportDate: new Date().toISOString()
    };
    
    const dataStr = JSON.stringify(exportObj, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `research-links-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleAddLink = async () => {
    if (!newUrl.trim()) return;

    // FIRST THING: UI changes immediately
    setIsProcessing(true);  // This triggers instant visual feedback
    setButtonClickTime(`Button clicked at ${new Date().toLocaleTimeString()}.${new Date().getMilliseconds()}ms`);
    setTimeout(() => setButtonClickTime(null), 3000); // Clear after 3 seconds
    
    const opId = logOperation({
      type: 'create',
      method: 'POST',
      endpoint: '/research-links',
      status: 'pending',
      details: `Adding new link: ${newUrl}`
    });
    
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/research-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: newUrl })
      });

      if (response.ok) {
        const newLink = await response.json();
        setLinks([newLink, ...links]);
        setNewUrl('');
        setSelectedLink(newLink);
        
        updateOperation(opId, {
          status: 'success',
          details: `Link added successfully. Starting AI processing...`
        });
        
        // Log AI processing start
        const aiOpId = logOperation({
          type: 'ai-process',
          endpoint: `/research-links/${newLink.id}`,
          status: 'pending',
          details: 'AI is analyzing the webpage content...'
        });
        
        // Poll for AI processing completion
        let pollCount = 0;
        const pollInterval = setInterval(async () => {
          pollCount++;
          
          const pollOpId = logOperation({
            type: 'poll',
            method: 'GET',
            endpoint: `/research-links/${newLink.id}`,
            status: 'pending',
            details: `Checking AI processing status (attempt ${pollCount})...`
          });
          
          try {
            const checkResponse = await fetch(`${API_CONFIG.API_URL}/research-links/${newLink.id}`);
            if (checkResponse.ok) {
              const updatedLink = await checkResponse.json();
              
              updateOperation(pollOpId, {
                status: 'success',
                details: updatedLink.processedAt ? 'AI processing complete!' : 'Still processing...'
              });
              
              if (updatedLink.processedAt) {
                setLinks(prev => prev.map(l => l.id === updatedLink.id ? updatedLink : l));
                setSelectedLink(updatedLink);
                
                updateOperation(aiOpId, {
                  status: 'success',
                  details: 'AI analysis completed successfully'
                });
                
                clearInterval(pollInterval);
              }
            } else {
              updateOperation(pollOpId, {
                status: 'error',
                error: 'Failed to check status'
              });
            }
          } catch (err) {
            console.error('Error polling for updates:', err);
            updateOperation(pollOpId, {
              status: 'error',
              error: err instanceof Error ? err.message : 'Poll failed'
            });
          }
        }, 2000);
        
        // Clear interval after 30 seconds
        setTimeout(() => {
          clearInterval(pollInterval);
          if (operations.find(op => op.id === aiOpId)?.status === 'pending') {
            updateOperation(aiOpId, {
              status: 'error',
              error: 'AI processing timeout'
            });
          }
        }, 30000);
      } else {
        updateOperation(opId, {
          status: 'error',
          error: `HTTP ${response.status}: ${response.statusText}`
        });
      }
    } catch (error) {
      console.error('Error adding link:', error);
      updateOperation(opId, {
        status: 'error',
        error: error instanceof Error ? error.message : 'Failed to add link'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (id: string) => {
    const link = links.find(l => l.id === id);
    const opId = logOperation({
      type: 'delete',
      method: 'DELETE',
      endpoint: `/research-links/${id}`,
      status: 'pending',
      details: `Deleting: ${link?.title || 'link'}`
    });
    
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/research-links/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setLinks(links.filter(link => link.id !== id));
        if (selectedLink?.id === id) {
          setSelectedLink(null);
        }
        updateOperation(opId, {
          status: 'success',
          details: 'Link deleted successfully'
        });
      } else {
        updateOperation(opId, {
          status: 'error',
          error: `HTTP ${response.status}`
        });
      }
    } catch (error) {
      console.error('Error deleting link:', error);
      updateOperation(opId, {
        status: 'error',
        error: error instanceof Error ? error.message : 'Delete failed'
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLinks.size === 0) return;
    
    const confirmDelete = window.confirm(`Delete ${selectedLinks.size} selected link(s)?`);
    if (!confirmDelete) return;
    
    const deletePromises = Array.from(selectedLinks).map(id =>
      fetch(`${API_CONFIG.API_URL}/research-links/${id}`, { method: 'DELETE' })
    );
    
    try {
      await Promise.all(deletePromises);
      setLinks(links.filter(link => !selectedLinks.has(link.id)));
      setSelectedLinks(new Set());
      setBulkMode(false);
      if (selectedLink && selectedLinks.has(selectedLink.id)) {
        setSelectedLink(null);
      }
    } catch (error) {
      console.error('Error bulk deleting links:', error);
    }
  };

  const toggleLinkSelection = (id: string) => {
    const newSelection = new Set(selectedLinks);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedLinks(newSelection);
  };

  const selectAllLinks = () => {
    if (selectedLinks.size === filteredLinks.length) {
      setSelectedLinks(new Set());
    } else {
      setSelectedLinks(new Set(filteredLinks.map(l => l.id)));
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleReprocess = async (id: string, useCustomPrompt: boolean = false) => {
    setReprocessingIds(new Set([...reprocessingIds, id]));
    
    const link = links.find(l => l.id === id);
    const opId = logOperation({
      type: 'update',
      method: 'POST',
      endpoint: `/research-links/${id}/reprocess`,
      status: 'pending',
      details: `Re-processing: ${link?.title || 'link'}${useCustomPrompt ? ' with custom prompt' : ''}`
    });
    
    try {
      const body: any = {};
      if (useCustomPrompt && customPrompt) {
        body.customPrompt = customPrompt;
      }
      
      const response = await fetch(`${API_CONFIG.API_URL}/research-links/${id}/reprocess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        updateOperation(opId, {
          status: 'success',
          details: 'Re-processing started, waiting for AI...'
        });
        
        // Poll for updates
        setTimeout(() => {
          const refreshOpId = logOperation({
            type: 'fetch',
            method: 'GET',
            endpoint: '/research-links',
            status: 'pending',
            details: 'Refreshing links after reprocess...'
          });
          
          fetchLinks().then(() => {
            updateOperation(refreshOpId, {
              status: 'success',
              details: 'Links refreshed'
            });
          });
          
          setReprocessingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
          });
        }, 3000);
      } else {
        updateOperation(opId, {
          status: 'error',
          error: `HTTP ${response.status}`
        });
      }
    } catch (error) {
      console.error('Error reprocessing link:', error);
      updateOperation(opId, {
        status: 'error',
        error: error instanceof Error ? error.message : 'Reprocess failed'
      });
      setReprocessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
    
    setShowCustomPrompt(null);
    setCustomPrompt('');
  };

  const saveNote = (linkId: string) => {
    if (!newNote.trim()) return;
    
    const note: Note = {
      id: crypto.randomUUID(),
      linkId,
      content: newNote,
      createdAt: new Date().toISOString()
    };
    
    setNotes(prev => ({
      ...prev,
      [linkId]: [...(prev[linkId] || []), note]
    }));
    
    setNewNote('');
    
    // In a real app, save to backend
    localStorage.setItem(`notes_${linkId}`, JSON.stringify([...(notes[linkId] || []), note]));
  };

  const updateNote = (linkId: string, noteId: string) => {
    setNotes(prev => ({
      ...prev,
      [linkId]: prev[linkId].map(note => 
        note.id === noteId 
          ? { ...note, content: editingNoteContent, updatedAt: new Date().toISOString() }
          : note
      )
    }));
    
    setEditingNote(null);
    setEditingNoteContent('');
    
    // Save to localStorage
    localStorage.setItem(`notes_${linkId}`, JSON.stringify(notes[linkId]));
  };

  const deleteNote = (linkId: string, noteId: string) => {
    setNotes(prev => ({
      ...prev,
      [linkId]: prev[linkId].filter(note => note.id !== noteId)
    }));
    
    // Save to localStorage
    const updatedNotes = notes[linkId].filter(note => note.id !== noteId);
    localStorage.setItem(`notes_${linkId}`, JSON.stringify(updatedNotes));
  };

  // Load notes from localStorage on mount
  useEffect(() => {
    links.forEach(link => {
      const savedNotes = localStorage.getItem(`notes_${link.id}`);
      if (savedNotes) {
        setNotes(prev => ({
          ...prev,
          [link.id]: JSON.parse(savedNotes)
        }));
      }
    });
  }, [links]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <Brain className="w-8 sm:w-10 h-8 sm:h-10 text-indigo-600" />
                Research Links
              </h1>
              <p className="text-gray-600">Drop links, get AI-powered insights and expansions</p>
            </div>
            <div className="mt-4 sm:mt-0 flex gap-2">
              <button
                onClick={() => setShowOperations(!showOperations)}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  showOperations
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Activity className="w-4 h-4" />
                Operations
                {operations.filter(op => op.status === 'pending').length > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-yellow-400 text-black text-xs rounded-full">
                    {operations.filter(op => op.status === 'pending').length}
                  </span>
                )}
              </button>
              {bulkMode && selectedLinks.size > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete ({selectedLinks.size})
                </button>
              )}
              <button
                onClick={() => {
                  setBulkMode(!bulkMode);
                  setSelectedLinks(new Set());
                }}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  bulkMode 
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {bulkMode ? 'Cancel' : 'Select'}
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filters
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <button
                onClick={exportData}
                disabled={links.length === 0}
                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="mt-4 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-gray-500" />
              <span className="text-gray-600">{links.length} links</span>
            </div>
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-500" />
              <span className="text-gray-600">{getAllTags().length} tags</span>
            </div>
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gray-500" />
              <span className="text-gray-600">
                {Object.values(notes).reduce((acc, n) => acc + n.length, 0)} notes
              </span>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 animate-in slide-in-from-top duration-200">
            <div className="space-y-4">
              {/* Tags */}
              {getAllTags().length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {getAllTags().map(tag => (
                      <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          selectedTags.has(tag)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort by</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSortBy('newest')}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                      sortBy === 'newest'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Newest First
                  </button>
                  <button
                    onClick={() => setSortBy('oldest')}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                      sortBy === 'oldest'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Oldest First
                  </button>
                  <button
                    onClick={() => setSortBy('title')}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                      sortBy === 'title'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Alphabetical
                  </button>
                </div>
              </div>
              
              {/* Clear Filters */}
              {selectedTags.size > 0 && (
                <button
                  onClick={() => {
                    setSelectedTags(new Set());
                  }}
                  className="text-sm text-indigo-600 hover:text-indigo-700"
                >
                  Clear all tags
                </button>
              )}
            </div>
          </div>
        )}

        {/* Input Section */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          {buttonClickTime && (
            <div className="mb-3 p-2 bg-yellow-100 border border-yellow-300 rounded-lg text-sm text-yellow-800 animate-pulse">
              🎯 {buttonClickTime} - Button instantly changed to "Processing..." with spinner
            </div>
          )}
          <div className="flex gap-3">
            <input
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !isProcessing && handleAddLink()}
              placeholder="Paste a link to explore..."
              className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={isProcessing}
            />
            <button
              onClick={handleAddLink}
              disabled={isProcessing || !newUrl.trim()}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                // THIS IS WHAT YOU SEE IMMEDIATELY (within ~16ms)
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Link2 className="w-5 h-5" />
                  Save Link
                </>
              )}
            </button>
          </div>
        </div>

        {/* Operations Panel */}
        {showOperations && (
          <div className="bg-gray-900 text-white rounded-xl shadow-lg p-4 mb-6 animate-in slide-in-from-top duration-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Database className="w-5 h-5 text-green-400" />
                API Operations Log
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAutoScroll(!autoScroll)}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                    autoScroll
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-700 text-gray-300'
                  }`}
                >
                  Auto-scroll
                </button>
                <button
                  onClick={() => setOperations([])}
                  className="px-3 py-1 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div className="bg-black rounded-lg p-3 max-h-64 overflow-y-auto font-mono text-xs space-y-1"
                 ref={(el) => {
                   if (el && autoScroll && operations.length > 0) {
                     el.scrollTop = 0;
                   }
                 }}>
              {operations.length === 0 ? (
                <div className="text-gray-500 text-center py-4">No operations yet. Try adding a link or refreshing!</div>
              ) : (
                operations.map(op => (
                  <div key={op.id} className="flex items-start gap-2 py-1 border-b border-gray-800 last:border-0">
                    <span className="text-gray-500 text-[10px] w-16 flex-shrink-0">
                      {new Date(op.timestamp).toLocaleTimeString()}
                    </span>
                    
                    <div className="flex items-center gap-1 w-16 flex-shrink-0">
                      {op.status === 'pending' && <Loader2 className="w-3 h-3 text-yellow-400 animate-spin" />}
                      {op.status === 'success' && <CheckCircle className="w-3 h-3 text-green-400" />}
                      {op.status === 'error' && <XCircle className="w-3 h-3 text-red-400" />}
                      
                      {op.type === 'fetch' && <ArrowDown className="w-3 h-3 text-blue-400" />}
                      {op.type === 'create' && <ArrowUp className="w-3 h-3 text-green-400" />}
                      {op.type === 'update' && <RefreshCw className="w-3 h-3 text-yellow-400" />}
                      {op.type === 'delete' && <Trash2 className="w-3 h-3 text-red-400" />}
                      {op.type === 'ai-process' && <Brain className="w-3 h-3 text-purple-400" />}
                      {op.type === 'poll' && <Clock className="w-3 h-3 text-gray-400" />}
                    </div>
                    
                    <div className="flex items-center gap-2 flex-1">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                        op.method === 'GET' ? 'bg-blue-900 text-blue-300' :
                        op.method === 'POST' ? 'bg-green-900 text-green-300' :
                        op.method === 'DELETE' ? 'bg-red-900 text-red-300' :
                        'bg-gray-800 text-gray-400'
                      }`}>
                        {op.method || op.type.toUpperCase()}
                      </span>
                      
                      <span className="text-gray-300">{op.endpoint}</span>
                      
                      {op.duration && op.status !== 'pending' && (
                        <span className="text-gray-500 text-[10px]">
                          ({op.duration}ms)
                        </span>
                      )}
                    </div>
                    
                    <div className="flex-1 text-right">
                      {op.details && (
                        <span className={`text-[11px] ${
                          op.status === 'success' ? 'text-green-400' :
                          op.status === 'error' ? 'text-red-400' :
                          'text-gray-400'
                        }`}>
                          {op.details}
                        </span>
                      )}
                      {op.error && (
                        <span className="text-[11px] text-red-400">
                          {op.error}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="mt-3 flex items-center justify-between text-[10px] text-gray-400">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Server className="w-3 h-3" />
                  API: {API_CONFIG.API_URL}
                </span>
                <span className="flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  Environment: {window.location.hostname.includes('localhost') ? 'Development' : 'Production'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-green-400" />
                  {operations.filter(op => op.status === 'success').length}
                </span>
                <span className="flex items-center gap-1">
                  <AlertCircle className="w-3 h-3 text-yellow-400" />
                  {operations.filter(op => op.status === 'pending').length}
                </span>
                <span className="flex items-center gap-1">
                  <XCircle className="w-3 h-3 text-red-400" />
                  {operations.filter(op => op.status === 'error').length}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
            <button
              onClick={fetchLinks}
              className="ml-2 text-red-600 underline hover:text-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Search Bar - Always Visible */}
        <div className="mb-6">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search titles, descriptions, URLs, or AI insights..."
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-lg"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
          {/* Links List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                Your Research {searchQuery || selectedTags.size > 0 ? `(${filteredLinks.length} results)` : ''}
              </h2>
              <div className="flex items-center gap-2">
                {bulkMode && filteredLinks.length > 0 && (
                  <button
                    onClick={selectAllLinks}
                    className="text-sm text-indigo-600 hover:text-indigo-700"
                  >
                    {selectedLinks.size === filteredLinks.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
                {!isLoading && links.length > 0 && (
                  <button
                    onClick={fetchLinks}
                    className="text-gray-400 hover:text-indigo-600 transition-colors"
                    title="Refresh links"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {isLoading ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <Loader2 className="w-8 h-8 mx-auto mb-3 text-indigo-600 animate-spin" />
                <p className="text-gray-500">Loading research links...</p>
              </div>
            ) : filteredLinks.length === 0 ? (
              searchQuery || selectedTags.size > 0 ? (
                <div className="bg-white rounded-xl p-8 text-center text-gray-500">
                  <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg font-medium">No links match your search</p>
                  {searchQuery && (
                    <p className="mt-2 text-sm">
                      No results for "<span className="font-semibold text-gray-700">{searchQuery}</span>"
                    </p>
                  )}
                  {selectedTags.size > 0 && (
                    <p className="mt-2 text-sm">
                      With tags: {Array.from(selectedTags).join(', ')}
                    </p>
                  )}
                  <div className="mt-4 space-x-2">
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="text-indigo-600 hover:text-indigo-700"
                      >
                        Clear search
                      </button>
                    )}
                    {selectedTags.size > 0 && (
                      <button
                        onClick={() => setSelectedTags(new Set())}
                        className="text-indigo-600 hover:text-indigo-700"
                      >
                        Clear tags
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl p-8 text-center text-gray-500">
                  <Link2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No links yet. Add your first research link above!</p>
                </div>
              )
            ) : (
              filteredLinks.map(link => (
                <div
                  key={link.id}
                  onClick={() => !bulkMode && setSelectedLink(link)}
                  className={`bg-white rounded-xl p-5 transition-all duration-200 ${
                    bulkMode ? '' : 'cursor-pointer hover:shadow-lg'
                  } ${
                    selectedLink?.id === link.id && !bulkMode ? 'ring-2 ring-indigo-500 shadow-lg' : 'shadow-md'
                  } ${
                    selectedLinks.has(link.id) ? 'ring-2 ring-indigo-400 bg-indigo-50' : ''
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    {bulkMode && (
                      <input
                        type="checkbox"
                        checked={selectedLinks.has(link.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleLinkSelection(link.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="mr-3 mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                    )}
                    <div className="flex items-start gap-3 flex-1">
                      {link.faviconUrl && (
                        <div className="flex-shrink-0">
                          <img 
                            src={link.faviconUrl} 
                            alt={link.siteName || 'Site favicon'}
                            className="w-8 h-8 rounded-lg object-contain bg-gray-50 p-1"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="flex-1 pr-2">
                        <div className="flex items-start gap-2">
                          <h3 className="font-semibold text-gray-900 flex-1">
                            {link.title || 'Processing...'}
                          </h3>
                          {link.siteName && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {link.siteName}
                            </span>
                          )}
                        </div>
                        {!link.processedAt && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
                            <Clock className="w-3 h-3" />
                            <span>Loading metadata...</span>
                          </div>
                        )}
                        {link.processedAt && !link.aiExpansion && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                            <Brain className="w-3 h-3" />
                            <span>AI analysis available</span>
                          </div>
                        )}
                        {link.aiExpansion && (
                          <>
                            <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
                              <CheckCircle className="w-3 h-3" />
                              <span>AI analyzed</span>
                            </div>
                            <div className="mt-2 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
                              <div className="text-xs font-semibold text-purple-700 mb-1 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                AI Insights
                              </div>
                              <div className="text-xs text-gray-700 line-clamp-3">
                                {link.aiExpansion.substring(0, 250)}...
                              </div>
                              <div className="mt-2 text-xs text-purple-600 font-medium">
                                Click to see full analysis →
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {reprocessingIds.has(link.id) ? (
                        <Loader2 className="w-4 h-4 text-indigo-600 animate-spin" />
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCustomPrompt(link.id);
                          }}
                          className="text-gray-400 hover:text-indigo-600 transition-colors"
                          title="Re-process with AI"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(link.id);
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Custom prompt dialog */}
                  {showCustomPrompt === link.id && (
                    <div className="mb-3 p-3 bg-indigo-50 rounded-lg" onClick={(e) => e.stopPropagation()}>
                      <div className="text-sm font-medium text-gray-700 mb-2">Custom AI Prompt (optional)</div>
                      <textarea
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="e.g., Focus on technical implementation details..."
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        rows={2}
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => handleReprocess(link.id, true)}
                          className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                        >
                          Reprocess
                        </button>
                        <button
                          onClick={() => handleReprocess(link.id, false)}
                          className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                        >
                          Use Default
                        </button>
                        <button
                          onClick={() => {
                            setShowCustomPrompt(null);
                            setCustomPrompt('');
                          }}
                          className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {link.description && (
                    <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                      {link.description}
                    </p>
                  )}
                  
                  {/* URL Display */}
                  <div className="mb-3 p-2 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-100">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 text-xs sm:text-sm text-indigo-600 hover:text-indigo-800 transition-colors group font-mono"
                    >
                      <ExternalLink className="w-4 h-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                      <span className="truncate break-all" title={link.url}>
                        {link.url.replace(/^https?:\/\/(www\.)?/, '')}
                      </span>
                    </a>
                  </div>
                  
                  {link.ogImage && !bulkMode && (
                    <div className="mb-3 rounded-lg overflow-hidden bg-gray-50">
                      <img 
                        src={link.ogImage} 
                        alt={link.title}
                        className="w-full h-32 object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.parentElement?.remove();
                        }}
                      />
                    </div>
                  )}

                  {/* UTM Parameters Display */}
                  {(link.utmSource || link.utmMedium || link.utmCampaign) && (
                    <div className="mb-3 p-2 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-semibold text-green-800">Campaign Tracking</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {link.utmSource && (
                          <div className="flex gap-1">
                            <span className="text-gray-600">Source:</span>
                            <span className="font-medium text-green-700">{link.utmSource}</span>
                          </div>
                        )}
                        {link.utmMedium && (
                          <div className="flex gap-1">
                            <span className="text-gray-600">Medium:</span>
                            <span className="font-medium text-green-700">{link.utmMedium}</span>
                          </div>
                        )}
                        {link.utmCampaign && (
                          <div className="flex gap-1 col-span-2">
                            <span className="text-gray-600">Campaign:</span>
                            <span className="font-medium text-green-700">{link.utmCampaign}</span>
                          </div>
                        )}
                        {link.utmContent && (
                          <div className="flex gap-1">
                            <span className="text-gray-600">Content:</span>
                            <span className="font-medium text-green-700">{link.utmContent}</span>
                          </div>
                        )}
                        {link.utmTerm && (
                          <div className="flex gap-1">
                            <span className="text-gray-600">Term:</span>
                            <span className="font-medium text-green-700">{link.utmTerm}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 text-xs text-gray-500 border-t pt-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(link.url, link.id);
                      }}
                      className="flex items-center gap-1 hover:text-indigo-600 transition-colors"
                    >
                      {copiedId === link.id ? (
                        <>
                          <Check className="w-3 h-3 text-green-500" />
                          <span className="text-green-500">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          Copy URL
                        </>
                      )}
                    </button>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(link.createdAt).toLocaleDateString()}
                    </span>
                    {link.tags.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {link.tags.length}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Selected Link Detail */}
          <div className="lg:sticky lg:top-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">AI Expansion</h2>
            {selectedLink ? (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="mb-4">
                  <div className="flex items-start gap-3 mb-3">
                    {selectedLink.faviconUrl && (
                      <img 
                        src={selectedLink.faviconUrl} 
                        alt={selectedLink.siteName || 'Site favicon'}
                        className="w-10 h-10 rounded-lg object-contain bg-gray-50 p-1"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{selectedLink.title}</h3>
                      {selectedLink.siteName && (
                        <p className="text-sm text-gray-500">{selectedLink.siteName}</p>
                      )}
                    </div>
                  </div>
                  <a
                    href={selectedLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {selectedLink.url}
                  </a>
                  
                  {selectedLink.ogImage && (
                    <div className="mt-4 rounded-lg overflow-hidden bg-gray-50">
                      <img 
                        src={selectedLink.ogImage} 
                        alt={selectedLink.title}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.parentElement?.remove();
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Detailed UTM Analytics */}
                  {(selectedLink.utmSource || selectedLink.utmData) && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-3">
                        <BarChart3 className="w-5 h-5 text-green-600" />
                        <h4 className="font-semibold text-gray-800">Marketing Attribution</h4>
                      </div>
                      
                      <div className="space-y-2">
                        {selectedLink.utmSource && (
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-green-500" />
                            <span className="text-sm text-gray-600">Traffic Source:</span>
                            <span className="text-sm font-medium bg-green-100 px-2 py-0.5 rounded">
                              {selectedLink.utmSource}
                            </span>
                          </div>
                        )}
                        
                        {selectedLink.utmMedium && (
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-blue-500" />
                            <span className="text-sm text-gray-600">Medium:</span>
                            <span className="text-sm font-medium bg-blue-100 px-2 py-0.5 rounded">
                              {selectedLink.utmMedium}
                            </span>
                          </div>
                        )}
                        
                        {selectedLink.utmCampaign && (
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-purple-500" />
                            <span className="text-sm text-gray-600">Campaign:</span>
                            <span className="text-sm font-medium bg-purple-100 px-2 py-0.5 rounded">
                              {selectedLink.utmCampaign}
                            </span>
                          </div>
                        )}
                        
                        {selectedLink.utmContent && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Content Variant:</span>
                            <span className="text-sm font-medium bg-amber-100 px-2 py-0.5 rounded">
                              {selectedLink.utmContent}
                            </span>
                          </div>
                        )}
                        
                        {selectedLink.utmTerm && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Keywords:</span>
                            <span className="text-sm font-medium bg-indigo-100 px-2 py-0.5 rounded">
                              {selectedLink.utmTerm}
                            </span>
                          </div>
                        )}
                        
                        {selectedLink.utmData && Object.keys(selectedLink.utmData).length > 5 && (
                          <details className="mt-3">
                            <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                              View all tracking parameters ({Object.keys(selectedLink.utmData).length})
                            </summary>
                            <div className="mt-2 p-2 bg-white rounded text-xs font-mono">
                              {Object.entries(selectedLink.utmData).map(([key, value]) => (
                                <div key={key} className="flex gap-2">
                                  <span className="text-gray-500">{key}:</span>
                                  <span className="text-gray-700">{value}</span>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {selectedLink.aiExpansion && (
                  <div className="space-y-4">
                    <div className="prose prose-sm max-w-none">
                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-indigo-600" />
                            AI Analysis
                          </h4>
                          <button
                            onClick={() => copyToClipboard(selectedLink.aiExpansion || '', selectedLink.id)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {copiedId === selectedLink.id ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <div className="text-gray-700 whitespace-pre-wrap">
                          {selectedLink.aiExpansion}
                        </div>
                      </div>
                    </div>

                    {selectedLink.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedLink.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                  </div>
                )}

                {/* Notes Section - Always visible */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                        <MessageSquare className="w-4 h-4" />
                        Notes
                      </h4>
                      
                      {/* Add new note */}
                      <div className="mb-4">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && saveNote(selectedLink.id)}
                            placeholder="Add a note..."
                            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          <button
                            onClick={() => saveNote(selectedLink.id)}
                            className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Display notes */}
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {notes[selectedLink.id]?.map(note => (
                          <div key={note.id} className="bg-gray-50 rounded-lg p-3">
                            {editingNote === note.id ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editingNoteContent}
                                  onChange={(e) => setEditingNoteContent(e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  rows={2}
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => updateNote(selectedLink.id, note.id)}
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    <Save className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingNote(null);
                                      setEditingNoteContent('');
                                    }}
                                    className="text-gray-600 hover:text-gray-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <p className="text-sm text-gray-700">{note.content}</p>
                                <div className="flex justify-between items-center mt-2">
                                  <span className="text-xs text-gray-500">
                                    {new Date(note.createdAt).toLocaleString()}
                                    {note.updatedAt && ' (edited)'}
                                  </span>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => {
                                        setEditingNote(note.id);
                                        setEditingNoteContent(note.content);
                                      }}
                                      className="text-gray-400 hover:text-indigo-600"
                                    >
                                      <Edit3 className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => deleteNote(selectedLink.id, note.id)}
                                      className="text-gray-400 hover:text-red-500"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )) || (
                          <p className="text-sm text-gray-500 italic">No notes yet</p>
                        )}
                      </div>
                    </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center text-gray-400">
                <Brain className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Select a link to view AI expansion</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}